import { StyleSheet, css } from 'aphrodite';
import { type FC, useEffect, useMemo, useState } from 'react';
import {
	AIAgentTemplateFilterProperty,
	AIAgentTemplateScope,
	AIConversationType,
} from '../../../extViewmodels/sdk/models/AI';
import { useAIAgentTemplateFilterInfiniteQuery } from '../../../queries/AIAgentTemplate/useAIAgentTemplateFilterInfiniteQuery';
import { Button } from '../../../web/components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
	ComboboxStatus,
} from '../../../web/components-v2/Combobox';

/**
 * Label for the no-template choice, which sends `null` upstream. Reads as "Default" when picking the
 * agent to run and "All agents" when filtering by the agent that ran.
 */
const DEFAULT_NONE_LABEL = 'Default';

const SEARCH_DEBOUNCE_MS = 250;

/** An entry in the list. The no-template entry uses an empty id, which no real GUID can collide with. */
type AgentOption = { id: string; name: string };

/**
 * The components-v2 `Combobox` wrapper types its props off the primitive without the generic, so every
 * item callback receives `unknown`. Narrow by shape rather than asserting, so a change in what the
 * combobox hands back surfaces as a null here instead of a runtime property access on undefined.
 */
const asOption = (value: unknown): AgentOption | null =>
	typeof value === 'object' && value !== null && 'id' in value && 'name' in value ? (value as AgentOption) : null;

export type CoachingAgentPickerProps = {
	selectedTemplateId: string | null;
	onChange: (templateId: string | null) => void;
	/**
	 * Called when `selectedTemplateId` is confirmed gone — the by-id lookup succeeded and matched
	 * nothing. Lets a caller drop a deleted template so coaching doesn't start against an id the
	 * server will reject. Absence from the loaded page is NOT enough to conclude this.
	 */
	onSelectionMissing?: () => void;
	/** Label for the "no specific agent" choice. Defaults to `Default`; a filter wants `All agents`. */
	noneLabel?: string;
};

/**
 * Searchable picker for a live-coaching AI agent template. Shared by the focused view's settings
 * panel, the standard queue's settings drawer, and the coaching review's agent filter — the first two
 * choose the agent to run, the third filters by the agent that ran, which is what `noneLabel` adapts.
 *
 * Opens showing the first page so the list is browsable without typing, then searches the server on
 * the `Name` criterion (a case-insensitive regex) once the rep types, because an account can hold far
 * more templates than one page — a plain dropdown over page one silently hides the rest. `Load more`
 * walks the remaining pages.
 *
 * Built on `Combobox` rather than `Autocomplete`: this is a select with search, so it needs a real
 * selected value, a trigger that opens the list on click, and `filter={null}` to leave filtering to
 * the server. `UserCombobox` (the rep filter beside it on the coaching review) follows the same shape.
 */
export const CoachingAgentPicker: FC<CoachingAgentPickerProps> = ({
	selectedTemplateId,
	onChange,
	onSelectionMissing,
	noneLabel = DEFAULT_NONE_LABEL,
}) => {
	// What the rep typed, which is NOT what the input displays: once an agent is picked the input shows
	// its name, and feeding that name back as a query would narrow the list to the agent already chosen
	// so the next open could never browse.
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');

	useEffect(() => {
		const handle = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(handle);
	}, [search]);

	const listQuery = useAIAgentTemplateFilterInfiniteQuery({
		filter: {
			criteria: [
				{ property: AIAgentTemplateFilterProperty.Scope, value: AIAgentTemplateScope.Account },
				{ property: AIAgentTemplateFilterProperty.ConversationType, value: AIConversationType.SalesLiveCoaching },
				...(debouncedSearch.trim()
					? [{ property: AIAgentTemplateFilterProperty.Name, value: debouncedSearch.trim() }]
					: []),
			],
		},
	});

	const templates = useMemo<AgentOption[]>(
		() => listQuery.data?.pages.flatMap(page => page.values).map(t => ({ id: t.id, name: t.name })) ?? [],
		[listQuery.data]
	);

	// A selection made earlier (or on another machine) can sit outside the loaded page or outside the
	// current search results, and the picker must still name it rather than claiming "Default". Fetch
	// that one template by id only while it is missing from the list.
	const selectedInList = templates.find(t => t.id === selectedTemplateId);
	const selectedQuery = useAIAgentTemplateFilterInfiniteQuery({
		filter: {
			criteria: [{ property: AIAgentTemplateFilterProperty.Id, value: selectedTemplateId ?? '' }],
		},
		enabled: Boolean(selectedTemplateId) && !selectedInList,
	});
	const selectedResolved = selectedQuery.data?.pages
		.flatMap(page => page.values)
		.find(t => t.id === selectedTemplateId);

	useEffect(() => {
		if (!selectedTemplateId || selectedInList || !selectedQuery.isSuccess || selectedResolved) {
			return;
		}
		onSelectionMissing?.();
	}, [selectedTemplateId, selectedInList, selectedQuery.isSuccess, selectedResolved, onSelectionMissing]);

	// The none option leads so clearing a selection is always one click away, even mid-search. Memoized
	// so the identity passed as `value` below stays stable across renders.
	const options = useMemo<AgentOption[]>(() => [{ id: '', name: noneLabel }, ...templates], [noneLabel, templates]);
	// Null rather than the none option when nothing is picked: passing the none option would put its
	// label in the input as real text, so the rep's next keystroke would append to "Default" instead of
	// starting a search. Null leaves the input empty and lets the placeholder carry the label.
	const selectedValue = useMemo<AgentOption | null>(
		() => selectedInList ?? (selectedResolved ? { id: selectedResolved.id, name: selectedResolved.name } : null),
		[selectedInList, selectedResolved]
	);

	// base-ui pushes the selected item's label through this callback whenever the value is applied — on
	// mount and after every pick — so an incoming value naming the selection is a display sync, not
	// something the rep typed. Searching for it would narrow the list to the agent already chosen.
	// Defensive on both counts — a pick comes from the loaded options, a by-id restore deliberately does
	// not — because base-ui's echo cannot be driven under jsdom, so only the browser exercises this.
	const handleInputValueChange = (value: string) => {
		if (value === selectedValue?.name || options.some(option => option.name === value)) {
			return;
		}
		setSearch(value);
	};

	const status = listQuery.isLoading
		? 'Loading agents…'
		: templates.length === 0
			? debouncedSearch.trim()
				? `No agents match "${debouncedSearch.trim()}"`
				: 'No AI agents configured for this account'
			: '';

	return (
		<Combobox
			items={options}
			value={selectedValue}
			onValueChange={(value: unknown) => onChange(asOption(value)?.id || null)}
			onInputValueChange={handleInputValueChange}
			// Items arrive already filtered by the server; base-ui's own filtering would re-filter the
			// response against the raw input and drop legitimate server matches.
			filter={null}
			itemToStringLabel={(item: unknown) => asOption(item)?.name ?? ''}
			isItemEqualToValue={(item: unknown, value: unknown) => asOption(item)?.id === asOption(value)?.id}
		>
			<ComboboxInput
				placeholder={noneLabel}
				aria-label='Choose AI Agent'
				triggerAriaLabel='Show AI agents'
				styles={[styles.input]}
			/>
			<ComboboxContent>
				{status ? <ComboboxStatus>{status}</ComboboxStatus> : null}
				<ComboboxList>
					{(item: unknown) => {
						const option = asOption(item);
						return option ? (
							<ComboboxItem key={option.id} value={option}>
								{option.name}
							</ComboboxItem>
						) : null;
					}}
				</ComboboxList>
				{listQuery.hasNextPage && (
					<div className={css(styles.footer)}>
						<Button
							variant='ghost'
							size='sm'
							disabled={listQuery.isFetchingNextPage}
							onClick={() => listQuery.fetchNextPage()}
						>
							{listQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
						</Button>
					</div>
				)}
			</ComboboxContent>
		</Combobox>
	);
};

const styles = StyleSheet.create({
	input: {
		width: '100%',
	},
	footer: {
		display: 'flex',
		justifyContent: 'center',
		padding: 4,
	},
});
